/**
 * Authentication Context
 * Team logins use Basic HTTP Authentication; customer accounts (email +
 * password) get a token from the backend and carry their subscription plan.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  accountRequest,
  ApiError,
  type AccountSession,
  type AccountSummary,
} from '../utils/accountApi';

interface User {
  username: string;
  email: string;
  token: string;
  accountType?: 'legacy' | 'account';
  account?: AccountSummary | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (username: string, password: string) => Promise<User>;
  signUp: (email: string, password: string, acceptTerms: boolean) => Promise<User>;
  signOut: () => void;
  confirmSignUp: (email: string, code: string) => Promise<User>;
  completeNewPassword: (email: string, newPassword: string) => Promise<User>;
  getToken: () => string | null;
  checkSession: () => void;
  refreshAccount: () => Promise<User | null>;
  setAccount: (account: AccountSummary) => void;
}

const USER_CREDENTIALS: Record<string, string> = {
  Mac: 'MacJobs2024!',
  Ben: 'BenJobs2024!',
  Blessing: 'BlessingJobs2024!',
};

const STORAGE_KEY = 'auth_user';

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const userFromSession = (session: AccountSession): User => ({
  username: session.account.email,
  email: session.account.email,
  token: session.token,
  accountType: 'account',
  account: session.account,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const storeUser = useCallback((nextUser: User | null) => {
    setUser(nextUser);
    if (nextUser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const checkSession = useCallback(() => {
    // Check if user is stored in localStorage
    const storedUser = localStorage.getItem(STORAGE_KEY);
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser) as User;
        setUser(userData);
      } catch (e) {
        console.error('Error parsing stored user:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setLoading(false);
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const signIn = async (username: string, password: string): Promise<User> => {
    setError(null);

    if (USER_CREDENTIALS[username] === password) {
      const userData: User = {
        username: username,
        email: username,
        token: btoa(`${username}:${password}`), // Basic auth token
        accountType: 'legacy',
      };
      storeUser(userData);
      return userData;
    }

    if (!username.includes('@')) {
      const errorMsg = 'Invalid username or password';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    try {
      const session = await accountRequest<AccountSession>('/auth/login', {
        method: 'POST',
        body: { email: username, password },
      });
      const userData = userFromSession(session);
      storeUser(userData);
      return userData;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Sign in failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const signUp = async (
    email: string,
    password: string,
    acceptTerms: boolean
  ): Promise<User> => {
    setError(null);
    try {
      const session = await accountRequest<AccountSession>('/auth/signup', {
        method: 'POST',
        body: { email, password, accept_terms: acceptTerms },
      });
      const userData = userFromSession(session);
      storeUser(userData);
      return userData;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Sign up failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const confirmSignUp = async (_email: string, _code: string): Promise<User> => {
    throw new Error('Sign up confirmation is not required.');
  };

  const completeNewPassword = async (
    _email: string,
    _newPassword: string
  ): Promise<User> => {
    throw new Error('Password change is not supported yet.');
  };

  const signOut = (): void => {
    storeUser(null);
  };

  const getToken = (): string | null => {
    if (user?.token) {
      return user.token;
    }

    const storedUser = localStorage.getItem(STORAGE_KEY);
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser) as User;
        return userData.token;
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }

    return null;
  };

  const setAccount = useCallback(
    (account: AccountSummary) => {
      setUser((current) => {
        if (!current || current.accountType !== 'account') return current;
        const nextUser = { ...current, account };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
        return nextUser;
      });
    },
    []
  );

  const refreshAccount = useCallback(async (): Promise<User | null> => {
    if (!user || user.accountType !== 'account') return user;
    try {
      const { account } = await accountRequest<{ account: AccountSummary }>(
        '/auth/me',
        { token: user.token }
      );
      const nextUser = { ...user, account };
      storeUser(nextUser);
      return nextUser;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        storeUser(null);
        return null;
      }
      return user;
    }
  }, [user, storeUser]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    completeNewPassword,
    getToken,
    checkSession,
    refreshAccount,
    setAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
